import { useCallback, useEffect, useState } from 'react'
import {
  useSelectedAudioInputDevice,
  useSelectedVideoInputDevice,
} from '../../Devices/useDevices'
import { logMessage } from '../../Logging/log'

const useAudienceTracks = () => {
  const selectedVideoInputDevice = useSelectedVideoInputDevice()
  const selectedAudioInputDevice = useSelectedAudioInputDevice()

  const [cameraAudioTrack, setCameraAudioTrack] =
    useState<MediaStreamTrack | null>(null)
  const [cameraVideoTrack, setCameraVideoTrack] =
    useState<MediaStreamTrack | null>(null)

  useEffect(() => {
    const getVideoTrack = async () => {
      cameraVideoTrack?.stop()
      if (!selectedVideoInputDevice) {
        setCameraVideoTrack(null)
        return
      }

      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: selectedVideoInputDevice,
            width: { ideal: 1920 },
          },
        })
        const mediaStreamTrack = mediaStream.getVideoTracks().at(0)
        if (mediaStreamTrack) {
          setCameraVideoTrack(mediaStreamTrack)
          return
        }
      } catch (err) {
        logMessage('Failed to get user video media for JoinToSpeak', {
          level: 'warning',
          extra: {
            reason: err,
          },
        })
      }
      setCameraVideoTrack(null)
    }

    getVideoTrack()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideoInputDevice])

  useEffect(() => {
    const getAudioTrack = async () => {
      cameraAudioTrack?.stop()
      if (!selectedAudioInputDevice) {
        setCameraAudioTrack(null)
        return
      }

      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: selectedAudioInputDevice,
          },
        })
        const mediaStreamTrack = mediaStream.getAudioTracks().at(0)
        if (mediaStreamTrack) {
          setCameraAudioTrack(mediaStreamTrack)
          return
        }
      } catch (err) {
        logMessage('Failed to get user audio media for JoinToSpeak', {
          level: 'warning',
          extra: {
            reason: err,
          },
        })
      }
      setCameraAudioTrack(null)
    }

    getAudioTrack()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAudioInputDevice])

  const removeAllAudienceTracks = useCallback(() => {
    if (!cameraVideoTrack && !cameraAudioTrack) return
    try {
      cameraAudioTrack?.stop()
      cameraVideoTrack?.stop()
    } catch (err) {
      logMessage('Failed to stop audience tracks', {
        level: 'error',
        extra: {
          reason: err,
        },
      })
    }

    setCameraAudioTrack(null)
    setCameraVideoTrack(null)
  }, [cameraVideoTrack, cameraAudioTrack])

  return {
    audienceAudioTrack: cameraAudioTrack,
    audienceVideoTrack: cameraVideoTrack,
    removeAllAudienceTracks,
  }
}

export { useAudienceTracks }
